import type { ImgHTMLAttributes } from "react";
import { useRouteLoaderData } from "react-router";
import type { Route } from "../+types/root";
import {
	buildStaticAssetImageUrls,
	type ImageTransformConfig,
} from "../utils/image-transform";

interface TransformedImageProps
	extends Omit<
		ImgHTMLAttributes<HTMLImageElement>,
		"src" | "srcSet" | "sizes" | "width" | "height"
	> {
	src: string;
	alt: string;
	width: number;
	height: number;
	mobile?: Pick<ImageTransformConfig, "width" | "height">;
	fit?: ImageTransformConfig["fit"];
	loading?: "lazy" | "eager";
	fetchPriority?: "high" | "low" | "auto";
}

/**
 * Static storefront image using Cloudflare Transform Images when configured.
 * Falls back to the original relative path when CLOUDFLARE_TRANSFORM_IMAGES_URL is unset.
 */
export function TransformedImage({
	src,
	alt,
	width,
	height,
	mobile,
	fit = "contain",
	loading = "lazy",
	fetchPriority,
	className,
	...props
}: TransformedImageProps) {
	const loaderData =
		useRouteLoaderData<Route.ComponentProps["loaderData"]>("root");
	const cloudflareBaseUrl = loaderData?.ENV?.CLOUDFLARE_TRANSFORM_IMAGES_URL;
	const desktop = { width, height, fit };
	const mobileConfig = mobile
		? { width: mobile.width, height: mobile.height, fit }
		: undefined;
	const urls = buildStaticAssetImageUrls(
		src,
		desktop,
		cloudflareBaseUrl,
		mobileConfig,
	);
	return (
		<img
			src={urls.src}
			srcSet={urls.srcSet || undefined}
			sizes={urls.sizes || undefined}
			alt={alt}
			width={width}
			height={height}
			loading={loading}
			fetchPriority={fetchPriority}
			className={className}
			{...props}
		/>
	);
}
